
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import List from '@/common/components/UI/List/index.vue'
import Modal from '@/common/components/UI/Modal/index.vue'

// module components
import ReportListItem from '@/modules/DispatcherModule/components/ReportListItem/index.vue'

// realisations
import { dispatchers_sectors } from '@/core/realisations/dispatchers_sectors.realisation'
import { dispatcher } from '@/core/realisations/dispatcher.realisation'
import { couriers } from '@/core/realisations/couriers.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { DispatcherSectorT } from '@/core/types/dispatchers_sector.types'
import { CourierReportT } from '@/core/types/dispatcher.types'

// composable
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-select': TheSelect,
        'the-button': TheButton,
        'failed-fetch': FailedFetch,
        list: List,
        //
        'report-list-item': ReportListItem,
        Modal,
    },
    setup() {
        const notifications = useNotifications()

        const filters_state = reactive({
            dispatchers_sector_id: null as number | null,
        })

        const state = reactive(new DataState<CourierReportT>({}))

        const loadReports = async () => {
            if (!filters_state.dispatchers_sector_id) {
                return
            }
            try {
                state.is_loading = true

                const response = await dispatcher.getCouriersReports(
                    filters_state.dispatchers_sector_id
                )

                state.list = response.data

                state.is_failed = false
                state.is_completed = true
            } catch (error) {
                state.failure.catchError(error)

                if (!state.failure.error_state.is_cancel) {
                    state.is_failed = true
                }
            } finally {
                if (!state.failure.error_state.is_cancel) {
                    state.is_loading = false
                }
            }
        }

        const data_state = reactive({
            dispatchers_sectors: new DataState<DispatcherSectorT>({}),
        })

        const loadDispatchersSectors = async () => {
            try {
                data_state.dispatchers_sectors.is_loading = true

                const response = await dispatchers_sectors.getList()
                data_state.dispatchers_sectors.list = response.data

                data_state.dispatchers_sectors.is_failed = false
                data_state.dispatchers_sectors.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.dispatchers_sectors.failure.catchError(error)

                if (!data_state.dispatchers_sectors.failure.error_state.is_cancel) {
                    data_state.dispatchers_sectors.is_failed = true
                }
            } finally {
                if (!data_state.dispatchers_sectors.failure.error_state.is_cancel) {
                    data_state.dispatchers_sectors.is_loading = false
                }
            }
        }

        onBeforeMount(loadDispatchersSectors)

        const onFiltersReset = () => {
            filters_state.dispatchers_sector_id = null
            state.list = []
            state.is_failed = false
        }

        const complete_state = reactive({
            is_loading: false,
            is_modal_opened: false,
            complete_item: null as CourierReportT | null,
            errors: [] as string[],
        })

        const resetCompleteState = () => {
            complete_state.is_modal_opened = false
            complete_state.complete_item = null
            complete_state.errors = []
        }

        const openCompleteModal = (errors: string[]) => {
            complete_state.errors = errors
            complete_state.is_modal_opened = true
        }

        const completeCourierDay = async (courier_report: CourierReportT) => {
            complete_state.complete_item = courier_report

            try {
                complete_state.is_loading = true

                const response = await couriers.closeDay(courier_report.courierId, null)
                const errors = response.data.errors

                if (errors.length > 0) {
                    openCompleteModal(errors)
                } else {
                    notifications.notify({
                        title: `День курьера ${courier_report.fullName} завершен`,
                        type: 'success',
                    })
                    resetCompleteState()
                    loadReports()
                }
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `При завершении дня курьера ${courier_report.fullName} произошла ошибка`,
                })
            } finally {
                complete_state.is_loading = false
            }
        }

        return {
            filters_state,
            state,
            loadReports,
            data_state,
            loadDispatchersSectors,
            onFiltersReset,
            completeCourierDay,
            complete_state,
            resetCompleteState,
        }
    },
})
